/**
 * How an API client reaches its server. In the desktop app the main process
 * sends the request and adds the server's token; elsewhere the client fetches
 * a base URL, normally a same-origin path that a proxy forwards with the token.
 */

import type { EmbeddedApiRequest, EmbeddedApiResponse } from "../adapters/electron";

export type ApiRequest = EmbeddedApiRequest;
export type ApiReply = EmbeddedApiResponse;
export type Send = (request: ApiRequest) => Promise<ApiReply>;

export type TransportErrorCode = "TIMEOUT" | "CONNECTION_ERROR" | "UNKNOWN_ERROR";

/** Set by each server on every reply it sends. */
const SERVER_HEADER = "satchel-server";

/** A reply made by something in front of the server, such as a proxy's error page. */
class NotFromServerError extends Error {
  override name = "NotFromServerError";
}

/**
 * @param apiToken For callers that reach the server directly (tests, scripts).
 * Browser code must not pass one: a token in a web bundle is public.
 */
export const fetchSender = (baseUrl: string, apiToken?: string): Send => {
  return async (request) => {
    const headers: Record<string, string> = {};
    if (apiToken) {
      headers.Authorization = `Bearer ${apiToken}`;
    }
    if (request.password !== undefined) {
      headers["X-Wallet-Password"] = request.password;
    }
    if (request.body !== undefined) {
      headers["Content-Type"] =
        typeof request.body === "string" ? "application/json" : "application/octet-stream";
    }
    const response = await fetch(`${baseUrl}${request.path}`, {
      method: request.method,
      headers,
      body: request.body,
      signal: AbortSignal.timeout(request.timeoutMs),
    });
    if (!response.headers.has(SERVER_HEADER)) {
      throw new NotFromServerError(`HTTP ${response.status} came from in front of the server`);
    }
    return { status: response.status, body: await response.text() };
  };
};

/** Classify an error thrown by fetch or relayed from the main process. */
export const transportError = (error: unknown): { code: TransportErrorCode; message: string } => {
  const name = error instanceof Error ? error.name : "";
  const message = error instanceof Error ? error.message : String(error);
  if (name === "TimeoutError" || name === "AbortError" || /TimeoutError|timed out/i.test(message)) {
    return { code: "TIMEOUT", message: "Request timed out" };
  }
  if (error instanceof NotFromServerError || /fetch|ECONNREFUSED|not initialized/i.test(message)) {
    return { code: "CONNECTION_ERROR", message: "Cannot connect to the server" };
  }
  return { code: "UNKNOWN_ERROR", message };
};

/** The `error` field of a JSON error body, or the status when there is none. */
export const replyErrorMessage = (reply: ApiReply): string => {
  try {
    const parsed = JSON.parse(reply.body) as { error?: unknown };
    if (typeof parsed.error === "string") {
      return parsed.error;
    }
  } catch {
    // Not a JSON error body; the status is all there is.
  }
  return `HTTP ${reply.status}`;
};
